import React from "react";
import dayjs from "dayjs";
import styled from "styled-components";
import utc from "dayjs/plugin/utc";
import { formatUtcToLocal } from "./utils";

dayjs.extend(utc);

const getNestedValue = (obj: any, depth = 3) => {
  let result = obj;
  let firstKey = "";
  for (let i = 0; i < depth; i++) {
    if (!result || typeof result !== "object") return undefined;
    firstKey = Object.keys(result)[0];
    result = result[firstKey];
  }
  return { result, firstKey };
};

const getAuditDetails = (parsed: any) => {
  if (Object.keys(parsed)?.[0] == "entry_0") {
    const auditObj = getNestedValue(Object.values(parsed)[0], 1);

    return {
      audit: auditObj?.result || {},
      section: auditObj?.firstKey || "unknown",
      isArray: true,
    };
  } else {
    const section = Object.keys(parsed)[0];

    return {
      audit: parsed[section] || {},
      section,
      isArray: false,
    };
  }
};

const renderCreateMessages = (newData: any[] = []) => (
  <ul>
    {newData?.map((item, idx) => {
      const [key, value] = Object?.entries(item)[0];
      const displayKey = key.replace(/_/g, " ");
      if (Array.isArray(value)) {
        return (
          <>
            <StyleInnerHeading>{displayKey}:-</StyleInnerHeading>
            {renderCreateMessages(value)}
          </>
        );
      } else {
        return (
          <li key={`${key}-${idx}`}>
            <strong>{displayKey}</strong>: <em>{String(value)}</em>
          </li>
        );
      }
    })}
  </ul>
);

const renderUpdateMessages = (oldData: any[] = [], newData: any[] = []) => {
  const toMap = (data: any[]) =>
    data &&
    Array.isArray(data) &&
    data?.reduce((acc, obj) => {
      const [key, value] = Object?.entries(obj)[0];
      acc[key] = value;
      return acc;
    }, {} as Record<string, any>);

  const oldMap = toMap(oldData);
  const newMap = toMap(newData);

  const changedKeys = Object?.keys({ ...oldMap, ...newMap })?.filter(
    (key) => oldMap[key] !== newMap[key]
  );

  if (changedKeys?.length === 0) return <span>No changes detected.</span>;

  return (
    <ul>
      {changedKeys?.map((key) => (
        <>
          {Array.isArray(oldMap[key]) ? (
            <>
              <StyleInnerHeading>{key}:-</StyleInnerHeading>
              {renderUpdateMessages(oldMap[key], newMap[key])}
            </>
          ) : (
            <>
              <li key={key}>
                <strong>{key} </strong> Modified <i> From</i>:{" "}
                <em>"{String(oldMap[key])}"</em> <i>To</i> :{" "}
                <em>"{String(newMap[key])}"</em>
              </li>
            </>
          )}
        </>
      ))}
    </ul>
  );
};

const GlobalAudit = ({ log = [] }: { log: any[] }) => {
  return (
    <>
      {log?.map((entry, index) => {
        let parsed: any = {};

        try {
          const normalizedJson = entry?.description
            .replace(/'/g, '"')
            .replace(/\bTrue\b/g, "true")
            .replace(/\bFalse\b/g, "false")
            .replace(/\bNone\b/g, "null");

          parsed = JSON.parse(normalizedJson);
        } catch (e) {
          console.error(
            "Invalid JSON format in description:",
            entry.description
          );
        }

        if (!parsed) return null;

        const { audit, section, isArray } = getAuditDetails(parsed);
        const oldData = audit.old_data || [];
        const newData = audit.new_data || [];

        return entry?.note_type?.toLowerCase() === "internal" ? (
          <Container key={index}>
            <h2>
              <strong>{entry?.created_by}</strong>
              <> added a note</>
            </h2>
            <div className="cover">
              <TextContainer>
                <>{entry?.description}</>
              </TextContainer>

              {/* <Divider /> */}

              <SubTextContainer>
                <MinContainer>
                  <Subtext1>{entry?.created_by}</Subtext1>
                  {entry?.created_at && (
                    <strong>
                      {formatUtcToLocal(entry.created_at).date}{" "}
                      {formatUtcToLocal(entry.created_at).time}
                    </strong>
                  )}
                </MinContainer>
              </SubTextContainer>
            </div>
          </Container>
        ) : (
          <Container key={index}>
            <h2>
              <strong>{entry?.updated_by}</strong>{" "}
              {oldData?.length > 0 ? (
                <>
                  updated a log on <strong>{section}</strong>
                </>
              ) : (
                <>
                  created a log on <strong>{section}</strong>
                </>
              )}
            </h2>
            <div className="cover">
              <TextContainer>
                {isArray ? (
                  <>
                    {Object.values(parsed).map((parse: any, idx: number) => {
                      const audit = getNestedValue(parse, 1)?.result;
                      const oldData = audit?.old_data || [];
                      const newData = audit?.new_data || [];

                      return (
                        <DivideNested key={idx}>
                          {oldData.length > 0
                            ? renderUpdateMessages(oldData, newData)
                            : renderCreateMessages(newData)}
                        </DivideNested>
                      );
                    })}
                  </>
                ) : (
                  <>
                    {oldData.length > 0
                      ? renderUpdateMessages(oldData, newData)
                      : renderCreateMessages(newData)}
                  </>
                )}
              </TextContainer>

              {/* <Divider /> */}

              <SubTextContainer>
                <MinContainer>
                  <Subtext1>{entry?.updated_by}</Subtext1>
                  {entry?.updated_at && (
                    <strong>
                      {formatUtcToLocal(entry.updated_at).date}{" "}
                      {formatUtcToLocal(entry.updated_at).time}
                    </strong>
                  )}
                </MinContainer>
              </SubTextContainer>
            </div>
          </Container>
        );
      })}
    </>
  );
};

export default GlobalAudit;

/* Styled Components (same as before) */
const Container = styled.div`
  padding: 10px;
  justify-content: space-between;
  border-radius: 10px;
  border: 1px solid rgba(199, 211, 225, 0.5);
  margin-bottom: 10px;
  h2 {
    padding-bottom: 4px;
    border-bottom: 1px solid rgba(199, 211, 225, 0.5);
    margin-bottom: 9px;
    font-size: 12px;
    strong {
      color: #1483de;
      font-family: Inter;
      font-size: 13px;
      font-style: normal;
      font-weight: 500;
    }
  }
  .cover {
    display: flex;
    justify-content: space-between;
  }
`;

const TextContainer = styled.div`
  color: #4f71a6;
  font-family: Inter;
  font-size: 13px;
  font-style: normal;
  font-weight: 400;
  line-height: 135%;
  flex-basis: 80%;

  strong {
    color: #1483de;
    font-family: Inter;
    font-size: 13px;
    font-style: normal;
    font-weight: 500;
  }
  i {
    font-weight: bold;
  }
`;

const SubTextContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: end;
  gap: 8px;
`;

const MinContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
  margin-top: 5px;
  color: #7899cd;
  font-family: Lato;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 12px;
`;

const Subtext1 = styled.div`
  color: #1483de;
  font-family: Inter;
  font-size: 13px;
  font-style: normal;
  font-weight: 500;
`;

const DivideNested = styled.div`
  margin-bottom: 12px;
`;
const StyleInnerHeading = styled.h2`
  color: #3494e3;
  font-size: 15px !important;
  font-weight: 800;
  margin: 5px;
  font-size: 14px;
  margin-left: 0;
`;
